import { type ReactNode, useId } from 'react';
import { Check } from 'lucide-react';

import { Checkbox } from '@/components/Checkbox/Checkbox';
import { Label } from '@/components/Label/Label';
import { Pager } from '@/components/Pagination/Pager';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import { cn } from '@/util/utils';

export interface PickerItem {
    id: string;
    name: string;
    /** The muted line: brand and category, or brand and code. */
    meta?: string;
    /** The right-hand column of a checkbox row: where a piece sits, or its source. */
    trailing?: string;
    /** The garment, drawn at the row's size. */
    art?: ReactNode;
    /** A row that cannot be chosen: already in whatever is being picked into. Tinted, never toggled, in either mode. */
    disabled?: boolean;
}

interface PickerListProps {
    /** The rows on this page, in order. */
    items: PickerItem[];
    picked: ReadonlySet<string>;
    onToggle: (item: PickerItem) => void;
    /**
     * `multiple` is the outfit editor's list (3144:70687): a checkbox, the garment on a muted
     * square, the name over its meta, and a trailing column. `single` is the piece editor's
     * catalogue list: one line of garment, name and meta, and a tick once chosen.
     */
    mode: 'multiple' | 'single';
    loading?: boolean;
    /** What the list says when nothing matches. */
    empty: ReactNode;
    /** The line under the list, "Showing 6 of 17,425 pieces". */
    range: string;
    page: number;
    pageSize: number;
    /** How many rows match altogether, across every page. */
    total: number;
    onPageChange: (page: number) => void;
    /** The list's own test id, which the suite reads the rows under. */
    testId?: string;
    /** A slot on each row's name, for a caller whose readers pick the name out of the row. */
    nameSlot?: string;
}

/**
 * One picker for every "choose from a long list" in a form (ALS-275): the outfit editor over the
 * wardrobe and the product database, and the piece editor over the database. Paged, so every
 * row can be reached without guessing a search term: the outfit picker used to stop at six with
 * "Showing 6 of 8 pieces" and no way to the other two, and the catalogue one stopped at eight
 * silently. The caller orders the rows (picked first) and fetches the page.
 */
function PickerList({
    items,
    picked,
    onToggle,
    mode,
    loading = false,
    empty,
    range,
    page,
    pageSize,
    total,
    onPageChange,
    testId,
    nameSlot = 'picker-name',
}: PickerListProps) {
    const idBase = useId();
    const pages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div data-slot="picker-list" className="flex flex-col gap-2">
            <div data-testid={testId} className="border-border overflow-hidden rounded-md border">
                {loading ? (
                    <div className="flex flex-col gap-2 p-3">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                    </div>
                ) : items.length === 0 ? (
                    <p className="text-muted-foreground p-4 text-xs">{empty}</p>
                ) : (
                    <ul className="flex list-none flex-col">
                        {items.map((item) => {
                            const on = picked.has(item.id);
                            const line = 'border-border border-b last:border-b-0';
                            if (mode === 'multiple') {
                                const id = `${idBase}-${item.id}`;
                                return (
                                    <li
                                        key={item.id}
                                        data-slot="picker-row"
                                        data-picked={on || undefined}
                                        data-disabled={item.disabled || undefined}
                                        className={cn(
                                            line,
                                            'flex items-center gap-3 px-3.5 py-2.5',
                                            (on || item.disabled) && 'bg-muted',
                                            item.disabled && 'text-muted-foreground',
                                        )}
                                    >
                                        <Checkbox
                                            id={id}
                                            checked={on}
                                            disabled={item.disabled}
                                            onCheckedChange={() => onToggle(item)}
                                        />
                                        {item.art ? (
                                            <span
                                                aria-hidden
                                                className="bg-muted flex size-[30px] shrink-0 items-center justify-center overflow-hidden rounded-xs p-1"
                                            >
                                                {item.art}
                                            </span>
                                        ) : null}
                                        <Label
                                            htmlFor={id}
                                            className="flex min-w-0 flex-1 flex-col items-start gap-[3px] font-normal"
                                        >
                                            <span
                                                data-slot={nameSlot}
                                                className="w-full truncate text-sm font-medium"
                                            >
                                                {item.name}
                                            </span>
                                            {item.meta ? (
                                                <span className="text-label-sm text-muted-foreground w-full truncate">
                                                    {item.meta}
                                                </span>
                                            ) : null}
                                        </Label>
                                        {item.trailing ? (
                                            <span className="text-label-sm text-muted-foreground hidden shrink-0 sm:inline">
                                                {item.trailing}
                                            </span>
                                        ) : null}
                                    </li>
                                );
                            }
                            return (
                                <li
                                    key={item.id}
                                    data-slot="picker-row"
                                    data-disabled={item.disabled || undefined}
                                    className={line}
                                >
                                    <button
                                        type="button"
                                        aria-pressed={on}
                                        disabled={item.disabled}
                                        onClick={() => onToggle(item)}
                                        className={cn(
                                            'flex w-full items-center gap-2.5 px-3 py-[7px] text-left pointer-coarse:min-h-11',
                                            'focus-visible:ring-outline outline-none focus-visible:ring-[3px] focus-visible:ring-inset',
                                            // Chosen and hovered are exclusive rather than layered: a
                                            // hover utility outranks a plain one, so a chosen row under
                                            // the cursor would draw the lighter of the two.
                                            on || item.disabled ? 'bg-muted' : 'hover:bg-muted/50',
                                            item.disabled && 'text-muted-foreground',
                                        )}
                                    >
                                        {item.art ? (
                                            <span aria-hidden className="flex size-[22px] shrink-0">
                                                {item.art}
                                            </span>
                                        ) : null}
                                        <span
                                            data-slot={nameSlot}
                                            className="min-w-0 shrink truncate text-sm"
                                        >
                                            {item.name}
                                        </span>
                                        {item.meta ? (
                                            <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                                                {item.meta}
                                            </span>
                                        ) : (
                                            <span className="flex-1" />
                                        )}
                                        {on ? (
                                            <Check className="size-3.5 shrink-0" aria-hidden />
                                        ) : null}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <span className="text-muted-foreground text-sm">{range}</span>
                {pages > 1 ? <Pager page={page} pages={pages} onChange={onPageChange} /> : null}
            </div>
        </div>
    );
}

export { PickerList };
